import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MapPin, User, Users, Truck, Plus, Copy, MapPin as PinIcon, Mail, BarChart3, Loader2 } from "lucide-react";
import { money, adminGet, adminPost, adminDelete, fetchDrivers } from "./utils";

/* --------------------- helpers --------------------- */

function KpiTile({ label, value }) {
  return (
    <div className="bg-white border border-brand-navy/10 rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">{label}</div>
      <div className="font-serif text-2xl text-brand-deep mt-1">{value}</div>
    </div>
  );
}

function RideMiniRow({ ride, highlight, muted }) {
  const isCompleted = ride.status === "completed" || !!ride.captured_amount_cents;
  const amountCents = isCompleted
    ? Number(ride.captured_amount_cents || 0)
    : Number(ride.hold_amount_cents || 0);
  const amountLabel = isCompleted ? "Collected" : "Hold";
  return (
    <div
      className={`rounded-2xl p-4 border ${
        highlight
          ? "bg-emerald-50 border-emerald-300"
          : muted
            ? "bg-brand-surfaceAlt/40 border-brand-navy/10"
            : "bg-white border-brand-navy/10"
      }`}
      data-testid={`driver-ride-${ride.id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-brand-green" />
          <span className="font-medium text-brand-deep text-sm">{ride.member_name}</span>
          {ride.driver_ride_status && (
            <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
              {ride.driver_ride_status.replace(/_/g, " ")}
            </span>
          )}
        </div>
        <div className="text-xs text-brand-muted font-mono">{ride.trip_date} · {ride.trip_time}</div>
      </div>
      <div className="text-xs text-brand-deep mt-1 truncate">
        <PinIcon className="w-3 h-3 inline text-emerald-600 mr-1" />
        {ride.pickup}
      </div>
      <div className="text-xs text-brand-muted mt-0.5 truncate">→ {ride.dropoff}</div>
      {amountCents > 0 && (
        <div className="mt-2 flex items-center justify-between text-xs pt-2 border-t border-brand-navy/10">
          <span className="text-brand-muted">
            {amountLabel} <strong className="text-brand-deep">{money(amountCents)}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

function DriverKpiDrawer({ auth, driverId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const sumCaptured = (rides) => rides.reduce((n, r) => n + (Number(r.captured_amount_cents) || 0), 0);
  const sumHold = (rides) => rides.reduce((n, r) => n + (Number(r.hold_amount_cents) || 0), 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminGet(auth, `drivers/${driverId}/routes`);
      setData(d);
    } catch (err) {
      toast.error(err.message || "Failed to load driver detail");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [auth, driverId, onClose]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose} data-testid="driver-kpi-drawer">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-brand-bg h-full overflow-y-auto shadow-2xl">
        {loading || !data ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-3 mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Driver detail</div>
                <h2 className="font-serif text-3xl text-brand-deep mt-1">{data.driver.name}</h2>
                <div className="text-sm text-brand-muted mt-1">
                  {data.driver.phone} · <span className="capitalize">{data.driver.on_ride ? "on a ride" : (data.driver.status === "available" ? "online" : "offline")}</span>
                </div>
                {(data.driver.car_year || data.driver.car_make) && (
                  <div className="text-xs text-brand-muted mt-1">
                    {[data.driver.car_year, data.driver.car_color, data.driver.car_make, data.driver.car_model].filter(Boolean).join(" ")}
                    {data.driver.car_tag ? ` · ${data.driver.car_tag}` : ""}
                  </div>
                )}
              </div>
              <button onClick={onClose} data-testid="close-driver-drawer" className="text-brand-muted hover:text-brand-navy">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <KpiTile label="Rides today" value={data.kpis.rides_today ?? 0} />
              <KpiTile label="30-day rides" value={data.kpis.rides_30d ?? 0} />
              <KpiTile label="30-day collected" value={data.kpis.captured_30d_cents != null ? money(data.kpis.captured_30d_cents) : "—"} />
              <KpiTile
                label={`Payout 30d (${data.kpis.days_worked_30d ?? 0} day${(data.kpis.days_worked_30d || 0) === 1 ? "" : "s"} × ${money(data.driver.daily_flat_rate_cents || 0)})`}
                value={money(data.kpis.payout_30d_cents || 0)}
              />
              <KpiTile
                label="Avg. rating (30d)"
                value={data.kpis.avg_stars_30d != null ? `★ ${data.kpis.avg_stars_30d} (${data.kpis.rated_30d})` : "—"}
              />
              <KpiTile
                label={`Avg trip time 30d${data.kpis.trips_timed_30d ? ` (${data.kpis.trips_timed_30d})` : ""}`}
                value={data.kpis.avg_trip_minutes_30d != null ? `${data.kpis.avg_trip_minutes_30d} min` : "—"}
              />
              <KpiTile
                label="Total on-trip time 30d"
                value={data.kpis.total_trip_minutes_30d ? `${(data.kpis.total_trip_minutes_30d / 60).toFixed(1)} hr` : "—"}
              />
            </div>

            <section className="mb-6">
              <h3 className="font-serif text-xl text-brand-deep mb-3">Live route</h3>
              {data.live ? <RideMiniRow ride={data.live} highlight /> : (
                <div className="bg-white border border-brand-navy/10 rounded-2xl p-4 text-sm text-brand-muted">
                  No active ride right now.
                </div>
              )}
            </section>

            <section className="mb-6">
              <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-serif text-xl text-brand-deep">Upcoming ({data.upcoming.length})</h3>
                {data.upcoming.length > 0 && (
                  <div className="text-xs text-brand-muted" data-testid="driver-upcoming-total">
                    Total holds: <strong className="text-brand-deep">{money(sumHold(data.upcoming))}</strong>
                  </div>
                )}
              </div>
              {data.upcoming.length === 0 ? (
                <div className="bg-white border border-brand-navy/10 rounded-2xl p-4 text-sm text-brand-muted">
                  Nothing else queued for this driver.
                </div>
              ) : (
                <div className="space-y-2" data-testid="driver-upcoming-list">
                  {data.upcoming.map((r) => <RideMiniRow key={r.id} ride={r} />)}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-serif text-xl text-brand-deep">Recent completed ({data.recent.length})</h3>
                {data.recent.length > 0 && (
                  <div className="text-xs text-brand-muted text-right" data-testid="driver-recent-totals">
                    Collected: <strong className="text-brand-deep">{money(sumCaptured(data.recent))}</strong>
                    <br />
                    Driver payout (30d):{" "}
                    <strong className="text-emerald-700">{money(data.kpis.payout_30d_cents || 0)}</strong>
                  </div>
                )}
              </div>
              {data.recent.length === 0 ? (
                <div className="bg-white border border-brand-navy/10 rounded-2xl p-4 text-sm text-brand-muted">
                  No completed rides yet.
                </div>
              ) : (
                <div className="space-y-2" data-testid="driver-recent-list">
                  {data.recent.map((r) => <RideMiniRow key={r.id} ride={r} muted />)}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function AddVehicleModal({ onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [color, setColor] = useState("");
  const [tag, setTag] = useState("");
  const [vehicleType, setVehicleType] = useState("sedan");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    await onSaved({
      label: label.trim(),
      year: year ? Number(year) : undefined,
      make: make || undefined, model: model || undefined,
      color: color || undefined, tag: tag || undefined,
      vehicle_type: vehicleType,
    });
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="add-vehicle-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Add fleet vehicle</div>
            <h3 className="font-serif text-2xl text-brand-deep mt-1">New vehicle</h3>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-navy"><XCircle className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input value={label} onChange={(e) => setLabel(e.target.value)} required placeholder="Label (e.g. Van #3)"
                 data-testid="veh-label" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          <select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}
                  data-testid="veh-type" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15">
            <option value="sedan">Sedan / SUV</option>
            <option value="van">Van</option>
            <option value="wheelchair_van">Wheelchair van</option>
            <option value="stretcher">Stretcher van</option>
          </select>
          <div className="grid grid-cols-3 gap-2">
            <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year"
                   data-testid="veh-year" type="number" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            <input value={color} onChange={(e) => setColor(e.target.value)} placeholder="Color"
                   data-testid="veh-color" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Tag"
                   data-testid="veh-tag" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={make} onChange={(e) => setMake(e.target.value)} placeholder="Make"
                   data-testid="veh-make" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
            <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model"
                   data-testid="veh-model" className="px-4 py-2.5 rounded-lg border border-brand-navy/15" />
          </div>
          <button type="submit" disabled={busy || !label.trim()} data-testid="veh-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add to fleet
          </button>
        </form>
      </div>
    </div>
  );
}

function VehicleCard({ v, drivers, onToggle }) {
  const [editing, setEditing] = useState(false);
  const assigned_ids = v.assigned_driver_ids || (v.assigned_driver_id ? [v.assigned_driver_id] : []);
  const assignedDrivers = assigned_ids.map((id) => drivers.find((d) => d.id === id)).filter(Boolean);
  return (
    <div data-testid={`vehicle-${v.id}`} className="border border-brand-navy/10 rounded-lg p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-brand-deep truncate">{v.label}</div>
          <div className="text-[11px] text-brand-muted truncate">
            {[v.year, v.color, v.make, v.model].filter(Boolean).join(" ") || v.vehicle_type}
            {v.tag ? ` · ${v.tag}` : ""}
          </div>
        </div>
        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full shrink-0 ${assignedDrivers.length ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
          {assignedDrivers.length}/2
        </span>
      </div>

      {!editing ? (
        <div className="mt-2 pt-2 border-t border-brand-navy/10 flex items-center justify-between gap-2">
          <div className="text-xs text-brand-deep min-w-0 flex-1" data-testid={`vehicle-summary-${v.id}`}>
            {assignedDrivers.length === 0 ? (
              <span className="text-brand-muted italic">Unassigned</span>
            ) : (
              <>
                <span className="text-brand-muted">Drivers: </span>
                <span className="text-emerald-700 font-medium">{assignedDrivers.map((d) => d.name).join(" · ")}</span>
              </>
            )}
          </div>
          <button
            onClick={() => setEditing(true)}
            data-testid={`vehicle-edit-${v.id}`}
            className="text-xs font-medium text-brand-navy hover:text-brand-green px-2 py-1 rounded-full border border-brand-navy/15 hover:border-brand-green shrink-0"
          >
            Edit
          </button>
        </div>
      ) : (
        <div className="mt-2 pt-2 border-t border-brand-navy/10 space-y-1">
          {drivers.filter((d) => d.active).map((d) => {
            const isOn = assigned_ids.includes(d.id);
            const disabled = !isOn && assigned_ids.length >= 2;
            return (
              <label key={d.id} className={`flex items-center gap-2 text-xs ${disabled ? "opacity-40" : "cursor-pointer"}`}>
                <input type="checkbox" checked={isOn} disabled={disabled}
                       onChange={() => onToggle(v.id, d.id, assigned_ids)}
                       data-testid={`veh-${v.id}-drv-${d.id}`} className="accent-brand-green" />
                <span className={isOn ? "text-emerald-700 font-medium" : "text-brand-deep"}>{d.name}</span>
              </label>
            );
          })}
          {drivers.filter((d) => d.active).length === 0 && (
            <div className="text-[11px] text-brand-muted italic">No active drivers to assign.</div>
          )}
          <button
            onClick={() => setEditing(false)}
            data-testid={`vehicle-save-${v.id}`}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium py-1.5 rounded-full transition"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Save &amp; close
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------- Main panel --------------------- */

export function DriversPanel({ auth }) {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRate, setNewRate] = useState("");
  const [openDriverId, setOpenDriverId] = useState(null);
  const [showAddVehicle, setShowAddVehicle] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, vRes] = await Promise.all([
        fetchDrivers(auth),
        adminGet(auth, "vehicles").catch(() => ({ vehicles: [] })),
      ]);
      setDrivers(dRes.drivers || []);
      setVehicles(vRes.vehicles || []);
    } catch (err) {
      toast.error(err.message || "Failed to load drivers");
    } finally {
      setLoading(false);
    }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const createDriver = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newPhone.trim()) return;
    setCreating(true);
    try {
      const rateCents = Math.round((parseFloat(newRate) || 0) * 100);
      const data = await adminPost(auth, "drivers", {
        name: newName.trim(),
        phone: newPhone.trim(),
        email: newEmail.trim() || undefined,
        daily_flat_rate_cents: rateCents,
      });
      toast.success(`${data.driver.name} added — invite code ${data.driver.invite_code}`);
      setNewName(""); setNewPhone(""); setNewEmail(""); setNewRate("");
      await load();
    } catch (err) {
      toast.error(err.message || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const resetCode = async (id) => {
    if (!window.confirm("Reset invite code? The driver will need to sign in again with the new code.")) return;
    try {
      const data = await adminPost(auth, `drivers/${id}/reset-code`);
      toast.success(`New code: ${data.invite_code}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Reset failed");
    }
  };

  const deactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}? They'll be signed out and unable to sign in until reactivated.`)) return;
    try {
      await adminPost(auth, `drivers/${id}/deactivate`);
      toast.success("Driver deactivated");
      await load();
    } catch (err) {
      toast.error(err.message || "Deactivate failed");
    }
  };

  const removeDriver = async (id, name) => {
    if (!window.confirm(`PERMANENTLY delete ${name}? This can't be undone. Any assigned fleet vehicles will be freed up.`)) return;
    try {
      await adminDelete(auth, `drivers/${id}`);
      toast.success(`${name} deleted`);
      await load();
    } catch (err) { toast.error(err.message || "Delete failed"); }
  };

  const setRate = async (id, name, currentCents) => {
    const current = (currentCents / 100).toFixed(0);
    const raw = window.prompt(`Set daily flat rate for ${name} (USD, per day worked)`, current);
    if (raw === null) return;
    const dollars = parseFloat(raw);
    if (Number.isNaN(dollars) || dollars < 0 || dollars > 2000) {
      toast.error("Rate must be a number between $0 and $2,000");
      return;
    }
    try {
      await adminPost(auth, `drivers/${id}/rate`, { daily_flat_rate_cents: Math.round(dollars * 100) });
      toast.success(`Rate updated to $${dollars.toFixed(2)}/day`);
      await load();
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  };

  const sendInvite = async (d) => {
    let addr = d.email || "";
    if (!addr) {
      addr = window.prompt(`Enter email address to send ${d.name}'s activation code`, "") || "";
      addr = addr.trim().toLowerCase();
      if (!addr || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
        toast.error("Please enter a valid email address");
        return;
      }
    }
    try {
      const res = await adminPost(auth, `drivers/${d.id}/send-invite`, { email: addr });
      if (res.status === "sent") {
        toast.success(`Invite emailed to ${res.email}`);
      } else {
        toast.info(`Email service unavailable. Share code ${res.invite_code} with ${res.email} manually.`);
      }
      await load();
    } catch (err) {
      toast.error(err.message || "Send failed");
    }
  };

  const toggleDriverOnVehicle = async (vehicleId, driverId, currentIds) => {
    const isOn = currentIds.includes(driverId);
    const next = isOn ? currentIds.filter((x) => x !== driverId) : [...currentIds, driverId];
    if (next.length > 2) { toast.error("Max 2 drivers per vehicle"); return; }
    try {
      await adminPost(auth, `vehicles/${vehicleId}/assign`, { driver_ids: next });
      toast.success(isOn ? "Driver removed from vehicle" : "Driver added to vehicle");
      await load();
    } catch (err) { toast.error(err.message || "Assign failed"); }
  };

  const addVehicle = async (fields) => {
    try {
      await adminPost(auth, "vehicles", fields);
      toast.success(`${fields.label} added to fleet`);
      setShowAddVehicle(false);
      await load();
    } catch (err) {
      toast.error(err.message || "Add failed");
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={createDriver} className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="add-driver-form">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-green mb-3">Add a driver</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Driver name"
                 data-testid="new-driver-name"
                 className="px-4 py-2.5 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green text-brand-deep" />
          <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Phone"
                 data-testid="new-driver-phone"
                 className="px-4 py-2.5 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green text-brand-deep" />
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Email (for invite)"
                 data-testid="new-driver-email"
                 className="px-4 py-2.5 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green text-brand-deep" />
          <input type="number" min={0} max={2000} step={10} value={newRate} onChange={(e) => setNewRate(e.target.value)}
                 placeholder="Daily rate $" data-testid="new-driver-rate"
                 className="px-4 py-2.5 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green text-brand-deep" />
          <button type="submit" disabled={creating || !newName.trim() || !newPhone.trim()} data-testid="add-driver-button"
                  className="inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium py-2.5 rounded-full disabled:opacity-60">
            <Plus className="w-4 h-4" /> Add driver
          </button>
        </div>
        <div className="text-[11px] text-brand-muted mt-2">Email is optional — used to send the driver their activation code &amp; login link. Daily rate is what they earn per day worked.</div>
      </form>

      <div className="bg-white border border-brand-navy/10 rounded-2xl p-6" data-testid="fleet-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-green">Fleet vehicles</div>
            <div className="text-[11px] text-brand-muted mt-0.5">
              Assign a fleet vehicle to drivers who don&apos;t have their own car. {vehicles.filter((v) => v.active).length} active · {vehicles.filter((v) => v.active && !v.assigned_driver_id).length} unassigned
            </div>
          </div>
          <button onClick={() => setShowAddVehicle(true)} data-testid="add-vehicle-btn"
                  className="inline-flex items-center gap-1.5 text-xs bg-brand-navy hover:bg-brand-navyDark text-white font-medium px-3 py-2 rounded-full">
            <Plus className="w-3.5 h-3.5" /> Add vehicle
          </button>
        </div>
        {vehicles.filter((v) => v.active).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
            {vehicles.filter((v) => v.active).map((v) => (
              <VehicleCard key={v.id} v={v} drivers={drivers} onToggle={toggleDriverOnVehicle} />
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-brand-muted text-sm">Loading drivers…</div>
      ) : drivers.length === 0 ? (
        <div className="bg-white border border-brand-navy/10 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-brand-surfaceAlt mx-auto flex items-center justify-center mb-4">
            <Truck className="w-6 h-6 text-brand-green" />
          </div>
          <h3 className="font-serif text-2xl text-brand-deep">No drivers yet</h3>
          <p className="text-sm text-brand-muted mt-2">Add your first driver above — they&apos;ll get an invite code to sign into the driver app at /driver.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drivers.map((d) => {
            // "On a ride" comes from the derived `on_ride` flag (driver has an
            // in_progress ride), never from d.status — that only means presence.
            const statusColor = d.on_ride ? "bg-amber-500"
              : d.status === "available" ? "bg-emerald-500"
              : "bg-slate-400";
            const statusLabel = d.on_ride ? "On a ride" : d.status === "available" ? "Online" : "Offline";
            return (
            <div key={d.id} className="bg-white border border-brand-navy/10 rounded-2xl p-5" data-testid={`driver-row-${d.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${statusColor}`}
                      data-testid={`driver-status-dot-${d.id}`}
                      title={statusLabel}
                    />
                    <span className="font-serif text-xl text-brand-deep">{d.name}</span>
                    {!d.active && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Inactive</span>}
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${d.on_ride ? "bg-amber-100 text-amber-800" : d.status === "available" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                      {statusLabel}
                    </span>
                    {d.vehicle_ownership === "own" && (
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200" data-testid={`ownership-badge-${d.id}`}>
                        Independent
                      </span>
                    )}
                    {d.vehicle_ownership === "company" && (
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200" data-testid={`ownership-badge-${d.id}`}>
                        Employee · Fleet
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-brand-muted mt-1">{d.phone}{d.email ? ` · ${d.email}` : ""}</div>
                  {d.daily_flat_rate_cents > 0 && (
                    <div className="text-xs text-emerald-700 mt-1 font-medium" data-testid={`driver-rate-${d.id}`}>
                      Daily rate: {money(d.daily_flat_rate_cents)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setOpenDriverId(d.id)} data-testid={`view-driver-${d.id}`}
                          className="inline-flex items-center gap-1.5 bg-brand-navy hover:bg-brand-navyDark text-white text-xs font-medium px-3 py-2 rounded-lg transition">
                    <BarChart3 className="w-3.5 h-3.5" /> Routes &amp; KPIs
                  </button>
                  <button onClick={() => copyCode(d.invite_code)} data-testid={`copy-code-${d.id}`}
                          className="inline-flex items-center gap-2 bg-brand-surfaceAlt hover:bg-brand-navy/10 text-brand-deep text-xs font-mono font-semibold px-3 py-2 rounded-lg transition">
                    <Copy className="w-3.5 h-3.5" /> {d.invite_code}
                  </button>
                  {d.active && (
                    <>
                      <button onClick={() => sendInvite(d)} data-testid={`send-invite-${d.id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg transition">
                        <Mail className="w-3.5 h-3.5" /> Send invite
                      </button>
                      <button onClick={() => setRate(d.id, d.name, d.daily_flat_rate_cents || 0)}
                              className="text-xs text-brand-navy hover:text-brand-green px-2 py-1" data-testid={`set-rate-${d.id}`}>Set rate</button>
                      <button onClick={() => resetCode(d.id)} className="text-xs text-brand-muted hover:text-brand-navy px-2 py-1">Reset code</button>
                      <button onClick={() => deactivate(d.id, d.name)} className="text-xs text-red-600 hover:text-red-700 px-2 py-1">Deactivate</button>
                    </>
                  )}
                  <button onClick={() => removeDriver(d.id, d.name)} data-testid={`delete-driver-${d.id}`}
                          className="text-xs text-red-700 hover:text-white hover:bg-red-700 px-2 py-1 rounded-full border border-red-300">
                    Delete
                  </button>
                </div>
              </div>
              {d.last_location && (
                <div className="mt-2 text-xs text-brand-muted flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  Last seen: {d.last_location.lat.toFixed(4)}, {d.last_location.lng.toFixed(4)} · {new Date(d.last_location.ts).toLocaleString()}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {openDriverId && (
        <DriverKpiDrawer auth={auth} driverId={openDriverId} onClose={() => setOpenDriverId(null)} />
      )}

      {showAddVehicle && (
        <AddVehicleModal onClose={() => setShowAddVehicle(false)} onSaved={addVehicle} />
      )}
    </div>
  );
}
